import { useState, useRef, useMemo } from 'react'
import {
  Download,
  Upload,
  Trash2,
  Database,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileDown,
  FileUp,
  Loader2,
  RotateCcw,
} from 'lucide-react'
import {
  useImportCSV,
  useExportEntities,
  useBulkDelete,
  EntityType,
  ExportFormat,
  getEntityTypeLabel,
  type BulkOperationResponse,
} from '@/features/bulk/api/useBulkOperations'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'

// ─── Constants ────────────────────────────────────────────────────────────────

const ENTITY_TYPE_OPTIONS: { value: EntityType; label: string }[] = [
  { value: EntityType.TESTCASE, label: 'Test Cases' },
  { value: EntityType.TESTSUITE, label: 'Test Suites' },
  { value: EntityType.PROJECT, label: 'Projects' },
  { value: EntityType.TESTPLAN, label: 'Test Plans' },
  { value: EntityType.REQUIREMENT, label: 'Requirements' },
  { value: EntityType.EXECUTION, label: 'Test Executions' },
]

const MAX_CSV_SIZE_MB = 10
const MAX_CSV_SIZE_BYTES = MAX_CSV_SIZE_MB * 1024 * 1024

// CSV column hints per entity type
const CSV_COLUMN_HINTS: Record<EntityType, string> = {
  [EntityType.TESTCASE]: 'name, summary, preconditions, importance, execution_type, status, project_id',
  [EntityType.TESTSUITE]: 'name, details, parent_id, project_id',
  [EntityType.PROJECT]: 'name, prefix, description, is_active',
  [EntityType.TESTPLAN]: 'name, notes, is_active, project_id',
  [EntityType.REQUIREMENT]: 'name, req_doc_id, srs_id',
  [EntityType.EXECUTION]: 'testcase_id, testplan_id, build_id, status, notes',
}

// ─── Operation Results Panel ──────────────────────────────────────────────────

function OperationResultsPanel({
  result,
  onDismiss,
}: {
  result: BulkOperationResponse
  onDismiss: () => void
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {result.failed === 0 ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            )}
            Operation Results
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={result.failed > 0 ? 'destructive' : 'default'}>
              {result.successful} / {result.total_items} successful
            </Badge>
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          {result.operation} on {getEntityTypeLabel(result.entity_type as EntityType)} — completed in {result.execution_time_ms.toFixed(0)}ms
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{result.total_items}</p>
                  <p className="text-xs text-muted-foreground">Total Items</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{result.successful}</p>
                  <p className="text-xs text-muted-foreground">Successful</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{result.failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error Details Table */}
        {result.errors.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-destructive">
              Errors ({result.errors.length})
            </h4>
            <div className="max-h-60 overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Index</TableHead>
                    <TableHead className="w-32">Item</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                   {result.errors.map((error: { index: number; item_id?: string; item?: string; error: string }, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">
                        #{error.index}
                      </TableCell>
                      <TableCell className="text-sm">
                        {error.item_id ?? error.item ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm text-destructive">
                        {error.error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BulkOperationsPage() {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>(EntityType.TESTCASE)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [deleteIds, setDeleteIds] = useState('')
  const [hardDelete, setHardDelete] = useState(false)
  const [lastResult, setLastResult] = useState<BulkOperationResponse | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const importCSVMutation = useImportCSV()
  const exportMutation = useExportEntities()
  const bulkDeleteMutation = useBulkDelete()

  // Parse delete IDs for count display
  const parsedDeleteIds = useMemo(() => {
    if (!deleteIds.trim()) return []
    return deleteIds
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id))
  }, [deleteIds])

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file (.csv extension required)')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    if (file.size > MAX_CSV_SIZE_BYTES) {
      toast.error(`File exceeds the ${MAX_CSV_SIZE_MB}MB limit. Please use a smaller file.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setSelectedFile(file)
  }

  async function handleImport() {
    if (!selectedFile) {
      toast.error('Please select a file to import')
      return
    }

    try {
      const result = await importCSVMutation.mutateAsync({
        entity_type: selectedEntityType,
        file: selectedFile,
      })

      const typedResult = result as BulkOperationResponse
      setLastResult(typedResult)
      toast.success(
        `Import completed: ${typedResult.successful} successful, ${typedResult.failed} failed`
      )

      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Import failed')
    }
  }

  async function handleExport(format: ExportFormat) {
    try {
      await exportMutation.mutateAsync({
        entity_type: selectedEntityType,
        format,
      })

      toast.success(
        format === ExportFormat.CSV
          ? 'CSV file download started'
          : 'JSON export completed'
      )
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Export failed')
    }
  }

  function handleDeleteClick() {
    if (!deleteIds.trim()) {
      toast.error('Please enter IDs to delete')
      return
    }
    if (parsedDeleteIds.length === 0) {
      toast.error('No valid numeric IDs found')
      return
    }
    setDeleteConfirmOpen(true)
  }

  async function handleBulkDelete() {
    try {
      const result = await bulkDeleteMutation.mutateAsync({
        entity_type: selectedEntityType,
        ids: parsedDeleteIds,
        hard_delete: hardDelete,
      })

      setLastResult(result)
      toast.success(
        `Bulk delete completed: ${result.successful} successful, ${result.failed} failed`
      )
      setDeleteIds('')
      setHardDelete(false)
      setDeleteConfirmOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Bulk delete failed')
      setDeleteConfirmOpen(false)
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Database className="h-8 w-8" />
          Bulk Operations
        </h1>
        <p className="text-muted-foreground mt-1">
          Import, export, and perform bulk operations on your test data
        </p>
      </div>

      {/* Global Warning */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Bulk operations can affect large amounts of data. Please review your selections carefully
          before proceeding. Maximum batch size: 10,000 items.
        </AlertDescription>
      </Alert>

      {/* Entity Type Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Entity Type</CardTitle>
          <CardDescription>
            Select the entity type for all bulk operations below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-sm">
            <Select
              value={selectedEntityType}
              onValueChange={(v) => setSelectedEntityType(v as EntityType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabbed Operations */}
      <Tabs defaultValue="import">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="import" className="gap-2">
            <Upload className="h-4 w-4" />
            Import
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </TabsTrigger>
          <TabsTrigger value="delete" className="gap-2">
            <Trash2 className="h-4 w-4" />
            Bulk Delete
          </TabsTrigger>
        </TabsList>

        {/* ─── Import Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="import">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUp className="h-5 w-5" />
                Import from CSV
              </CardTitle>
              <CardDescription>
                Upload a CSV file to bulk create {getEntityTypeLabel(selectedEntityType).toLowerCase()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="csv-file">CSV File</Label>
                <Input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                />
                {selectedFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: <span className="font-medium">{selectedFile.name}</span>{' '}
                    ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <Alert>
                <AlertDescription className="space-y-2">
                  <p className="font-medium">CSV Format Requirements:</p>
                  <p className="text-sm">
                    First row must contain column headers. Use UTF-8 encoding. Maximum file size: {MAX_CSV_SIZE_MB}MB. Maximum rows: 10,000.
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Expected columns for {getEntityTypeLabel(selectedEntityType)}:</span>{' '}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      {CSV_COLUMN_HINTS[selectedEntityType]}
                    </code>
                  </p>
                </AlertDescription>
              </Alert>

              <Button
                onClick={handleImport}
                disabled={!selectedFile || importCSVMutation.isPending}
              >
                {importCSVMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Import CSV
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Export Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileDown className="h-5 w-5" />
                Export Data
              </CardTitle>
              <CardDescription>
                Export {getEntityTypeLabel(selectedEntityType).toLowerCase()} to CSV or JSON format.
                Exports are automatically scoped to your tenant with a maximum of 10,000 records per export.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileDown className="h-4 w-4" />
                      Export as CSV
                    </CardTitle>
                    <CardDescription>
                      Download data in CSV format for use with Excel, Google Sheets, or other spreadsheet tools
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => handleExport(ExportFormat.CSV)}
                      disabled={exportMutation.isPending}
                      className="w-full"
                    >
                      {exportMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Export CSV
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileDown className="h-4 w-4" />
                      Export as JSON
                    </CardTitle>
                    <CardDescription>
                      Download data in JSON format for API integration, scripting, or data migration
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => handleExport(ExportFormat.JSON)}
                      disabled={exportMutation.isPending}
                      variant="secondary"
                      className="w-full"
                    >
                      {exportMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Export JSON
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Delete Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="delete">
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Bulk Delete
              </CardTitle>
              <CardDescription>
                Delete multiple {getEntityTypeLabel(selectedEntityType).toLowerCase()} at once.
                Soft delete is the default — records can be recovered. Hard delete is permanent.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {hardDelete
                    ? 'HARD DELETE is enabled. Records will be permanently removed from the database with no possibility of recovery.'
                    : 'Soft delete is active. Records will be marked as deleted but can be recovered if needed.'}
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="delete-ids">
                  Entity IDs (comma-separated)
                </Label>
                <Textarea
                  id="delete-ids"
                  placeholder="e.g., 101, 102, 103, 104, 105"
                  value={deleteIds}
                  onChange={(e) => setDeleteIds(e.target.value)}
                  rows={3}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-muted-foreground">
                  Enter the IDs of {getEntityTypeLabel(selectedEntityType).toLowerCase()} you want to delete, separated by commas.
                  {parsedDeleteIds.length > 0 && (
                    <span className="font-medium"> {parsedDeleteIds.length} valid ID(s) detected.</span>
                  )}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hard-delete"
                  checked={hardDelete}
                  onCheckedChange={(checked) => setHardDelete(checked === true)}
                />
                <Label htmlFor="hard-delete" className="cursor-pointer text-sm">
                  Hard Delete — permanently remove from database (cannot be undone)
                </Label>
              </div>

              <Button
                onClick={handleDeleteClick}
                disabled={parsedDeleteIds.length === 0 || bulkDeleteMutation.isPending}
                variant="destructive"
              >
                {bulkDeleteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete {parsedDeleteIds.length} {getEntityTypeLabel(selectedEntityType)}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Operation Results */}
      {lastResult && (
        <OperationResultsPanel
          result={lastResult}
          onDismiss={() => setLastResult(null)}
        />
      )}

      {/* ─── Delete Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirm Bulk Delete
            </DialogTitle>
            <DialogDescription>
              You are about to {hardDelete ? 'permanently delete' : 'soft-delete'}{' '}
              <span className="font-semibold">{parsedDeleteIds.length}</span>{' '}
              {getEntityTypeLabel(selectedEntityType).toLowerCase()}.
              {hardDelete && ' This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm font-medium">Entity Type</p>
              <p className="text-sm text-muted-foreground">{getEntityTypeLabel(selectedEntityType)}</p>
            </div>
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm font-medium">IDs to Delete</p>
              <p className="text-sm text-muted-foreground font-mono">
                {parsedDeleteIds.length <= 20
                  ? parsedDeleteIds.join(', ')
                  : `${parsedDeleteIds.slice(0, 20).join(', ')} ... and ${parsedDeleteIds.length - 20} more`}
              </p>
            </div>
            {hardDelete && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  HARD DELETE is enabled. Records will be permanently removed with no recovery option.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Confirm Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
